import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { date, mixed, number, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { Campo, Editor, Selecao } from 'Component/Form';
import Modal from 'Component/Modal';
import Tarefa from 'Interface/Tarefas';
import { useRecuperarUsuariosQuery } from 'Service';
import {
  TIPOS_DE_TAREFAS,
  TIPOS_DE_TAREFAS_PRODUCAO_DE_UA,
} from 'View/constants';
import { useAdicionarTarefaMutation } from 'View/Service/Default/Tarefas';

interface Props {
  exibir: boolean;
  aoFechar: (estado: boolean) => void;
  tarefa?: Tarefa;
}

const regras = object({
  tipo: string().required('Obrigatório'),
  atendente_id: number().required('Obrigatório'),
  titulo: string().required('Obrigatório'),
  descricao: mixed().test('obrigatorio', 'Obrigatório', (valor) => {
    if (valor?.blocks?.length && valor.blocks[0].text) {
      return true;
    }

    return false;
  }),
  prazo: date().required('Obrigatório'),
});

const Adicionar = ({ exibir, aoFechar, tarefa }: Props): JSX.Element => {
  const toast = useToast();
  const navigation = useNavigate();
  const [adicionarTarefa] = useAdicionarTarefaMutation();

  const { data: { usuarios } = {}, refetch } = useRecuperarUsuariosQuery({
    finder: 'listaJson',
    options: { permissao: ['super', 'admin', 'conteudista'] },
    limit: 500,
    sort: 'nome',
  });

  const enviar = async (dados: Tarefa): Promise<void> => {
    try {
      await adicionarTarefa({ ...dados, dados: { ...tarefa?.dados } }).unwrap();

      toast({
        tema: 'success',
        conteudo: 'Tarefa criada com sucesso!',
      });

      if (tarefa) {
        navigation('/admin/tarefas');
      } else {
        aoFechar(false);
      }
    } catch (erro) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const tipos = useMemo(() => {
    switch (tarefa?.tipo) {
      case 'producao-de-ua':
        return TIPOS_DE_TAREFAS_PRODUCAO_DE_UA;
      default:
        return TIPOS_DE_TAREFAS;
    }
  }, [tarefa]);

  return (
    <>
      <Modal titulo="Adicionar tarefa" exibir={exibir} aoFechar={aoFechar}>
        <Form onSubmit={enviar} regras={regras}>
          <Selecao opcoes={tipos} name="tipo" rotulo="Tipos de tarefa" />
          <Selecao
            opcoes={usuarios}
            name="atendente_id"
            rotulo="Atendente"
            acrescentarBotao={
              <Botao icone="arrow-clockwise" onClick={refetch} />
            }
          />
          <Campo name="titulo" rotulo="Título" />
          <Campo
            type="datetime-local"
            name="prazo"
            rotulo="Prazo para conclusão"
          />
          <Editor name="descricao" rotulo="Descrição" />
          <div className="d-flex justify-content-end gap-2">
            <Botao type="submit" texto="Enviar" />
            <Botao
              tema="light"
              texto="Cancelar"
              onClick={() => aoFechar(false)}
            />
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default Adicionar;
